import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@deepguide-ai/ui/button';
import { Checkbox } from '@deepguide-ai/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function ServiceNowConfigModal({ open, onOpenChange, integration, workspaceId }: ConfigModalProps) {
  const integrationId = integration?.id ?? '';
  const [draft, setDraft] = useState<{ sourceRef: string; ids: string[] } | null>(null);
  const [offset, setOffset] = useState(0);
  const [starting, setStarting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const scope = useQuery({ ...trpc.integrations.getServiceNowScope.queryOptions({ integrationId }),
    enabled: open && !!integrationId, refetchInterval: open && integrationId ? 3000 : false });
  const sourceRef = scope.data?.sourceRef;
  const groups = useQuery({ ...trpc.integrations.getServiceNowGroups.queryOptions({ integrationId }), enabled: open && !!integrationId });
  const tickets = useQuery({ ...trpc.integrations.getServiceNowTickets.queryOptions({ integrationId, sourceRef: sourceRef ?? '', offset }), enabled: open && !!integrationId && !!sourceRef });
  const save = useMutation(trpc.integrations.saveServiceNowScope.mutationOptions());
  const sync = useMutation(trpc.integrations.syncServiceNowTickets.mutationOptions());
  const selected = draft && draft.sourceRef === sourceRef ? draft.ids : scope.data?.assignmentGroupIds ?? [];
  const changed = [...selected].sort().join(',') !== [...(scope.data?.assignmentGroupIds ?? [])].sort().join(',');
  const busy = !!scope.data?.syncState && !['completed', 'failed'].includes(scope.data.syncState);
  const unavailable = selected.filter(id => groups.data && !groups.data.some(group => group.id === id));
  const error = scope.error || groups.error || tickets.error;

  useEffect(() => {
    setDraft(null);
    setOffset(0);
    setStartError(null);
  }, [open, integrationId]);
  useEffect(() => {
    setOffset(0);
    void queryClient.invalidateQueries({ queryKey: trpc.integrations.getServiceNowTickets.queryKey() });
  }, [sourceRef, scope.data?.lastSyncedAt]);

  const saveAndCollect = async () => {
    setStarting(true);
    setStartError(null);
    let saved = false;
    try {
      await save.mutateAsync({ integrationId, assignmentGroupIds: selected });
      saved = true;
      setDraft(null);
      await scope.refetch();
      await queryClient.invalidateQueries({ queryKey: trpc.integrations.getServiceNowTickets.queryKey() });
      await sync.mutateAsync({ integrationId });
      await scope.refetch();
      toast.success('ServiceNow collection queued');
    } catch (err) {
      setStartError(`${saved ? 'Selection saved, but collection could not be queued.' : 'Could not confirm the selection was saved.'} ${err instanceof Error ? err.message : 'Try again.'}`);
    } finally {
      setStarting(false);
    }
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-3xl'>
      <DialogHeader>
        <DialogTitle>ServiceNow incidents and changes</DialogTitle>
        <DialogDescription>Read incidents, changes, journals, current approvals and available field-change audit records from selected assignment groups.</DialogDescription>
      </DialogHeader>
      {!integrationId ? <Button className='mx-6 mb-6' disabled={connecting} onClick={async () => {
        setConnecting(true);
        try { await ServiceNowProvider.handleConnect({ workspaceId, onFinish: () => setConnecting(false) }); }
        catch { setConnecting(false); }
      }}>{connecting ? 'Connecting…' : 'Connect ServiceNow'}</Button> : <div className='space-y-5 px-6 pb-6'>
        {scope.data ? <p className='text-sm text-muted-foreground'>Instance: {scope.data.instanceUrl}</p> : null}
        {error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive text-sm'>{error.message}</p>
          <Button variant='outline' disabled={scope.isFetching || groups.isFetching || tickets.isFetching} onClick={() => {
            void scope.refetch(); void groups.refetch(); void tickets.refetch();
          }}>Retry loading</Button>
        </div> : null}
        <fieldset className='space-y-2' disabled={busy || starting || !scope.data || !!scope.error || groups.isLoading || !!groups.error}>
          <legend className='mb-2 text-sm font-medium'>Assignment groups</legend>
          {scope.isLoading || groups.isLoading ? <p className='text-sm text-muted-foreground'>Loading assignment groups…</p> : null}
          {groups.data?.length === 0 ? <p className='text-sm text-muted-foreground'>No accessible assignment groups. Check the connected account’s table and field access.</p> : null}
          <div className='max-h-48 space-y-2 overflow-y-auto'>
            {groups.data?.map(group => <label key={group.id} className='flex items-center gap-2 text-sm'>
              <Checkbox checked={selected.includes(group.id)} onCheckedChange={checked => setDraft({ sourceRef: sourceRef!, ids: checked === true ? [...selected, group.id] : selected.filter(id => id !== group.id) })} />
              {group.name}
            </label>)}
            {unavailable.map(id => <label key={id} className='flex items-center gap-2 text-sm'>
              <Checkbox checked onCheckedChange={() => setDraft({ sourceRef: sourceRef!, ids: selected.filter(value => value !== id) })} />
              Unavailable saved group: {id}
            </label>)}
          </div>
        </fieldset>
        <p className='text-sm text-muted-foreground'>{changed ? 'Unsaved selection. Collected records below still reflect the saved selection.' : `${selected.length} assignment groups saved.`}</p>
        <div className='flex flex-wrap items-center gap-3'>
          <Button disabled={busy || starting || !scope.data || !!scope.error || !selected.length || groups.isLoading || !!groups.error || !!unavailable.length} onClick={() => void saveAndCollect()}>
            {busy ? 'Collecting…' : starting ? 'Starting…' : 'Save groups and collect'}
          </Button>
          <p role='status' className='text-sm text-muted-foreground'>{busy ? 'Collection runs in the background. You can close this dialog.' : scope.data?.lastSyncedAt ? `Last collected ${new Date(scope.data.lastSyncedAt).toLocaleString()}` : 'No completed collection for this selection.'}</p>
        </div>
        {scope.data?.lastSyncedAt && scope.data.lastSyncCount === 0 ? <p className='text-sm text-muted-foreground'>The last collection returned no records. Retained snapshots below may be older.</p> : null}
        {startError ? <p role='alert' className='text-destructive text-sm'>{startError}</p> : null}
        {scope.data?.syncState === 'failed' || scope.data?.lastError ? <p role='alert' className='text-destructive text-sm'>Collection failed. {scope.data.lastError ?? 'Retry collection or check connection access.'} Previously collected data is retained.</p> : null}
        <section className='space-y-3 border-t pt-4'>
          <h3 className='text-sm font-medium'>Collected incidents and changes</h3>
          <p className='text-sm text-muted-foreground'>Journals and current approval states are not immutable audit history or proof of approved remediation. Field-change history depends on access, auditing and retention settings. Native state values depend on your instance configuration.</p>
          {tickets.isLoading ? <p className='text-sm'>Loading collected records…</p> : null}
          {tickets.data?.tickets.length === 0 ? <p className='text-sm text-muted-foreground'>No collected records for the saved selection.</p> : null}
          {tickets.data?.tickets.map(ticket => <details key={ticket.sourceId} className='rounded-md border p-3'>
            <summary className='cursor-pointer text-sm font-medium'>{ticket.title} · State: {ticket.status}</summary>
            <div className='mt-3 space-y-3 text-sm'>
              <p>Assignment group: {groups.data?.find(group => group.id === ticket.assignmentGroupId)?.name ?? ticket.assignmentGroupId}</p>
              <p>Assignee ID: {ticket.assigneeId ?? 'Unassigned or unavailable'}</p>
              <p className='text-xs text-muted-foreground'>Collected {new Date(ticket.collectedAt).toLocaleString()}. Retained snapshots may include records later moved or deleted in ServiceNow.</p>
              <p className='whitespace-pre-wrap break-words'>{ticket.content}</p>
            </div>
          </details>)}
          <div className='flex gap-2'>
            <Button variant='outline' disabled={!offset || tickets.isFetching} onClick={() => setOffset(current => Math.max(0, current - 25))}>Previous</Button>
            <Button variant='outline' disabled={!tickets.data?.hasMore || tickets.isFetching || !!tickets.error} onClick={() => setOffset(current => current + 25)}>Next</Button>
          </div>
        </section>
      </div>}
    </DialogContent>
  </Dialog>;
}

export const ServiceNowProvider: IntegrationProvider = {
  id: 'ServiceNow', name: 'ServiceNow', description: 'Read access inventories and scoped incidents and changes',
  Icon: () => <img src={vendorLogoPath('servicenow')} alt='' className='size-6' />,
  ConfigModal: ServiceNowConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [{ id: 'tickets', name: 'Incidents and changes', description: 'Read-only tickets, journals, current approvals and field-change history', enabled: true }],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initServiceNowAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start ServiceNow authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'servicenow-auth', provider: 'ServiceNow',
      onSuccess, onFinish, onError: message => toast.error(message),
      onPopupBlocked: () => toast.error('Allow popups to connect ServiceNow.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeServiceNowAuth.mutationOptions()).execute({ code, state });
    await queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
    return result;
  },
  syncData: async integration => {
    await queryClient.getMutationCache().build(queryClient, trpc.integrations.syncServiceNowTickets.mutationOptions()).execute({ integrationId: integration.id });
    await queryClient.invalidateQueries({ queryKey: trpc.integrations.getServiceNowScope.queryKey() });
  },
};
